// Browser push notification helpers for streak reminders (no external service).
// Requests permission and schedules a client-side timer that fires ~22 hours
// after last activity, warning the user their streak is at risk.

const PERMISSION_ASKED_KEY = 'wellth_notif_permission_asked';

export const isSupported = () => typeof window !== 'undefined' && 'Notification' in window;

export const currentPermission = () => (isSupported() ? Notification.permission : 'unsupported');

export const shouldPromptForPermission = () => {
  if (!isSupported()) return false;
  if (Notification.permission !== 'default') return false;
  return !localStorage.getItem(PERMISSION_ASKED_KEY);
};

export const markPromptShown = () => {
  localStorage.setItem(PERMISSION_ASKED_KEY, new Date().toISOString());
};

export const requestPermission = async () => {
  if (!isSupported()) return 'unsupported';
  markPromptShown();
  try {
    return await Notification.requestPermission();
  } catch {
    return 'default';
  }
};

let reminderTimer = null;

export const clearReminder = () => {
  if (reminderTimer) { clearTimeout(reminderTimer); reminderTimer = null; }
};

const showNotification = (title, body) => {
  if (!isSupported() || Notification.permission !== 'granted') return;
  try {
    const n = new Notification(title, {
      body,
      icon: '/favicon.ico',
      tag: 'wellth-streak-reminder',
      renotify: true,
    });
    n.onclick = () => { window.focus(); n.close(); };
  } catch { /* Safari on some platforms throws — ignore */ }
};

/**
 * Schedule a streak-at-risk reminder ~22 hours after `lastActiveISO`.
 * If already past that window (i.e., streak is about to break within 4 hours),
 * fire immediately.
 */
export const scheduleStreakReminder = ({ streakDays, lastActiveISO }) => {
  clearReminder();
  if (!isSupported() || Notification.permission !== 'granted') return;
  if (!streakDays || streakDays < 1 || !lastActiveISO) return;

  const last = new Date(`${lastActiveISO}T12:00:00Z`).getTime(); // treat as midday UTC for stability
  const now = Date.now();
  const twentyTwoHours = 22 * 60 * 60 * 1000;
  const fireAt = last + twentyTwoHours;
  const delay = Math.max(0, fireAt - now);
  const cap = 24 * 60 * 60 * 1000; // no more than 24h in the future

  if (delay > cap) return; // will be re-scheduled next visit

  reminderTimer = setTimeout(() => {
    showNotification(
      `Your ${streakDays}-day streak needs you 🔥`,
      "Just 60 seconds to keep the flame alive. Open Wellth to log today's care.",
    );
  }, delay);
};
